import { formatCurrency } from 'utils/formatCurrency'

// Sample SMS shown in the Transaction Alert card's phone preview. Templates are ported
// verbatim from the KMP app's files/customer_messages.xml (sms_customer_credit /
// sms_supplier_credit / sms_due / sms_advance), so the bubble reads in the CUSTOMER's
// language, as on iOS/Android. Mixed "₹" vs "Rs." prefixes are deliberate — do not normalise.
interface SmsTemplates {
  customerCredit: string
  supplierCredit: string
  due: string
  advance: string
}

const TEMPLATES: Record<string, SmsTemplates> = {
  en: {
    customerCredit:
      'OkCredit: Credit of ₹{amount} from {name}.\nBalance: ₹{balance} {type}\nDetails: okcr.in/app',
    supplierCredit:
      'OkCredit: Credit of Rs. {amount} to {name}.\nBalance: {balance} {type}\nDetails: okcr.in/app',
    due: 'Due',
    advance: 'Advance'
  },
  hi: {
    customerCredit:
      'OkCredit: ₹{amount} की राशि {name} से जमा हुई है।\nशेष राशि: ₹{balance} {type}\nविवरण: okcr.in/app',
    supplierCredit:
      'OkCredit: Rs. {amount} का क्रेडिट {name} को।\nBalance: {balance} {type}\nDetails: okcr.in/app',
    due: 'उधार',
    advance: 'जमा हुई राशि'
  },
  mr: {
    customerCredit:
      'OkCredit: {name} कडून ₹{amount} जमा झाले आहे.\nशिल्लक: ₹{balance} {type}\nतपशील: okcr.in/app',
    supplierCredit:
      'OkCredit: Rs. {amount} क्रेडिट {name} ला.\nBalance: {balance} {type}\nDetails: okcr.in/app',
    due: 'देय रक्कम',
    advance: 'आगाऊ रक्कम'
  },
  afh: {
    customerCredit:
      'OkCredit: ₹{amount} credit hua hai {name} se.\nBalance: ₹{balance} {type}\nDetails: okcr.in/app',
    supplierCredit:
      'OkCredit: Rs. {amount} ka credit {name} ko.\nBalance: {balance} {type}\nDetails: okcr.in/app',
    due: 'Udhaar',
    advance: 'Advance'
  },
  gu: {
    customerCredit:
      'OkCredit: {name} તરફથી ₹{amount} જમા થયું છે.\nબેલેન્સ: ₹{balance} {type}\nવિગતો: okcr.in/app',
    supplierCredit:
      'OkCredit: Rs. {amount} નું ક્રેડિટ {name} ને.\nBalance: {balance} {type}\nDetails: okcr.in/app',
    due: 'બાકી',
    advance: 'એડવાન્સ'
  },
  ta: {
    customerCredit:
      'OkCredit: {name} இலிருந்து ₹{amount} கிரெடிட் செய்யப்பட்டது.\nமீதி: ₹{balance} {type}\nவிவரம்: okcr.in/app',
    supplierCredit:
      'OkCredit: Rs. {amount} கடன் {name} க்கு.\nBalance: {balance} {type}\nDetails: okcr.in/app',
    due: 'நிலுவை',
    advance: 'அட்வான்ஸ்'
  },
  te: {
    customerCredit:
      'OkCredit: {name} నుండి ₹{amount} క్రెడిట్ అయింది.\nబ్యాలెన్స్: ₹{balance} {type}\nవివరాలు: okcr.in/app',
    supplierCredit:
      'OkCredit: Rs. {amount} క్రెడిట్ {name} కు.\nBalance: {balance} {type}\nDetails: okcr.in/app',
    due: 'బకాయి',
    advance: 'అడ్వాన్స్'
  },
  pa: {
    customerCredit:
      'OkCredit: {name} ਤੋਂ ₹{amount} ਜਮ੍ਹਾ ਹੋਇਆ ਹੈ।\nਬੈਲੈਂਸ: ₹{balance} {type}\nਵੇਰਵਾ: okcr.in/app',
    supplierCredit:
      'OkCredit: Rs. {amount} ਦਾ ਕ੍ਰੈਡਿਟ {name} ਨੂੰ.\nBalance: {balance} {type}\nDetails: okcr.in/app',
    due: 'ਬਾਕੀ',
    advance: 'ਐਡਵਾਂਸ'
  },
  ml: {
    customerCredit:
      'OkCredit: {name} നിന്നും ₹{amount} ക്രെഡിറ്റ് ചെയ്തു.\nബാലൻസ്: ₹{balance} {type}\nവിശദാംശങ്ങൾ: okcr.in/app',
    supplierCredit:
      'OkCredit: Rs. {amount} ക്രെഡിറ്റ് {name}-ന്.\nBalance: {balance} {type}\nDetails: okcr.in/app',
    due: 'കടം',
    advance: 'അഡ്വാൻസ്'
  },
  kn: {
    customerCredit:
      'OkCredit: {name} ರಿಂದ ₹{amount} ಕ್ರೆಡಿಟ್ ಆಗಿದೆ.\nಬಾಕಿ ಮೊತ್ತ: ₹{balance} {type}\nವಿವರಗಳು: okcr.in/app',
    supplierCredit:
      'OkCredit: Rs. {amount} ಕ್ರೆಡಿಟ್ {name} ಗೆ.\nBalance: {balance} {type}\nDetails: okcr.in/app',
    due: 'ಬಾಕಿ',
    advance: 'ಮುಂಗಡ'
  },
  bn: {
    customerCredit:
      'OkCredit: {name} থেকে ₹{amount} ক্রেডিট হয়েছে।\nব্যালেন্স: ₹{balance} {type}\nবিস্তারিত: okcr.in/app',
    supplierCredit:
      'OkCredit: Rs. {amount} এর ক্রেডিট {name} কে।\nBalance: {balance} {type}\nDetails: okcr.in/app',
    due: 'বাকি রয়েছে',
    advance: 'অ্যাডভান্স'
  }
}

// Same fixed sample figures as the mobile apps: a ₹200 credit leaving ₹200 due.
const SAMPLE_TXN_AMOUNT = 200
const SAMPLE_BALANCE = -200

// KMP trims the name to 12 chars for Hindi, 20 otherwise, so the bubble stays short.
const HINDI_NAME_CHAR_LIMIT = 12
const DEFAULT_NAME_CHAR_LIMIT = 20

export const buildTransactionSmsSample = ({
  isSupplier,
  langKey,
  name
}: {
  isSupplier: boolean
  langKey: string
  name: string
}): string => {
  const templates = TEMPLATES[langKey] || TEMPLATES.en
  const limit = langKey === 'hi' ? HINDI_NAME_CHAR_LIMIT : DEFAULT_NAME_CHAR_LIMIT
  const trimmedName = (name || '').length <= limit ? name || '' : (name || '').slice(0, limit)
  const template = isSupplier ? templates.supplierCredit : templates.customerCredit
  const balanceType = SAMPLE_BALANCE > 0 ? templates.advance : templates.due
  return template
    .replace('{amount}', formatCurrency(SAMPLE_TXN_AMOUNT))
    .replace('{name}', trimmedName)
    .replace('{balance}', formatCurrency(Math.abs(SAMPLE_BALANCE)))
    .replace('{type}', balanceType)
}
